import React, { useCallback, useMemo, useState } from 'react';
import Proptypes from 'prop-types';
import { useSelector } from 'react-redux';

import GlyphChartLines from '../../assets/svgs/GlyphChartLines';

import translate from '../../libs/i18n';
import compareArrays from '../../utils/compareArrays';
import roundToPrecision from '../../utils/roundToPrecision';
import toTitleCase from '../../utils/toTitleCase';
import { glyphDefaultProps } from '../../utils/defaultProps';
import { glyphShape } from '../../utils/propTypesShapes';

import { GlyphChartStyle } from './GlyphChartStyle';

const GlyphChart = ({ glyph, chartOnly, section }) => {
  const language = useSelector((state) => state.settings.language);

  const [allPercents, setAllPercents] = useState([]);
  const [rendered, setRendered] = useState([]);

  const getPathD = (p, x, y) =>
    `M 1 0 A 1 1 0 ${p > 0.5 ? 1 : 0} 1 ${x} ${y} L 0 0`;

  const renderSliceSvg = useCallback(
    (value, type, r, p, x, y, label) => {
      if (!value || value <= 0) {
        return null;
      }

      let svgClassName = '';

      if (section?.length && type !== 'fix') {
        svgClassName = section === `${label}-${type}` ? ' active' : ' disabled';
      }

      return (
        <svg
          className={`single-slice slice-${type}${svgClassName}`}
          viewBox="-1 -1 2 2"
          style={{ width: `${r * 100}%` }}
        >
          <path d={getPathD(p, x, y)} />
        </svg>
      );
    },
    [section]
  );

  const renderChartSection = useCallback(
    (data, label, order, percents) => {
      if (!data.percent || (!data.low && !data.normal && !data.high)) {
        return null;
      }

      const currentRendered = rendered;

      if (!currentRendered.includes(label)) {
        currentRendered.push(label);
        setRendered(currentRendered);
      }

      const low = roundToPrecision(data.low || 0, 6);
      const normal = roundToPrecision(data.normal || 0, 6);
      const high = roundToPrecision(data.high || 0, 6);
      const initialMainPercent = roundToPrecision(percents[0] || 0, 6);
      const initialMainPercentHalfAngle = roundToPrecision(
        (360 * initialMainPercent) / 2,
        4
      );
      const percent = roundToPrecision(data.percent || 0, 6);
      const labelRotate = roundToPrecision((360 * percent) / 2, 6);
      const x = Math.cos(2 * Math.PI * percent);
      const y = Math.sin(2 * Math.PI * percent);
      let percentFix = 1;
      let rotationAmout = -90;

      percentFix -= low;
      percentFix -= normal;
      percentFix -= high;

      const normalRadius = percentFix + normal;
      const highRadius = normalRadius + high;
      const lowRadius = highRadius + low;

      if (label != 'LU') {
        for (let i = 0; i < order; i += 1) {
          rotationAmout += 360 * percents[i];
          rotationAmout += percent > 0.5 ? 180 : 0;
        };
      };

      return (
        <div
          className="circles-wrapper"
          style={{
            transform: `rotate(${
              rotationAmout
            }deg)`,
          }}
        >
          <div className="chart-slices-wrapper">
            {renderSliceSvg(
              low,
              'low',
              lowRadius,
              percent,
              x,
              y,
              label.toLowerCase()
            )}
            {renderSliceSvg(
              high,
              'high',
              highRadius,
              percent,
              x,
              y,
              label.toLowerCase()
            )}
            {renderSliceSvg(
              normal,
              'normal',
              normalRadius,
              percent,
              x,
              y,
              label.toLowerCase()
            )}
            {renderSliceSvg(
              percentFix,
              'fix',
              percentFix,
              percent,
              x,
              y,
              label.toLowerCase()
            )}
          </div>
          {order !== 0 ? <span className="slice-outline" /> : null}
          {order === 4 ? (
            <span
              className="slice-outline"
              style={{
                transform: `translate(-50%, -75%) rotate(${
                  270 - rotationAmout
                }deg)`,
              }}
            />
          ) : null}
          {!chartOnly ? (
            <div
              className="glypho-label"
              style={{ transform: `rotate(${labelRotate}deg)` }}
            >
              <span
                className="glypho-label-text"
                style={{
                  transform: `translateY(-50%) rotate(${-(
                    rotationAmout +
                    labelRotate
                  )}deg)`,
                }}
              >
                {label}
              </span>
            </div>
          ) : null}
        </div>
      );
    },
    [chartOnly, renderSliceSvg, rendered]
  );

  const renderAllChartSections = useMemo(() => {
    const { lu, ll, rl, rm, ru } = glyph;

    if (ll && rl && rm && ru && lu) {
      const currentAllPercents = [
        lu.percent || 0,
        ll.percent || 0,
        rl.percent || 0,
        rm.percent || 0,
        ru.percent || 0,
      ];

      if (!compareArrays(currentAllPercents, allPercents)) {
        setAllPercents(currentAllPercents);
      }
      
      return (
        <>
          {renderChartSection(lu, 'LU', 0, currentAllPercents)}
          {renderChartSection(ll, 'LL', 1, currentAllPercents)}
          {renderChartSection(rl, 'RL', 2, currentAllPercents)}
          {renderChartSection(rm, 'RM', 3, currentAllPercents)}
          {renderChartSection(ru, 'RU', 4, currentAllPercents)}
        </>
      );
    }

    return null;
  }, [glyph, allPercents, renderChartSection]);

  return (
    <GlyphChartStyle className={chartOnly ? 'chart-only' : ''}>
      <div className="glyph-content-wrapper">
        <div className="glyph-content">
          {renderAllChartSections}
          <div
            className={`glypho-default-lines${
              rendered.length <= 0 ? ' disabled' : ''
            }`}
          >
            <GlyphChartLines
              fix={rendered.length > 0 ? 0 : 2}
              radius={100}
              hideLastCircle={rendered.length > 0}
            />
          </div>
        </div>
      </div>
      {!chartOnly ? (
        <ul className="glyph-legend-list">
          <li className="glyph-legend-item low">
            {toTitleCase(translate('lowFemale', language))}
          </li>
          <li className="glyph-legend-item normal">Normal</li>
          <li className="glyph-legend-item high">
            {toTitleCase(translate('highFemale', language))}
          </li>
        </ul>
      ) : null}
    </GlyphChartStyle>
  );
};

GlyphChart.propTypes = {
  chartOnly: Proptypes.bool,
  glyph: glyphShape,
  section: Proptypes.string,
};

GlyphChart.defaultProps = {
  chartOnly: false,
  glyph: glyphDefaultProps,
  section: '',
};

export default GlyphChart;
